import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTags } from 'react-tag-autocomplete';

const MULTI_VALUE_SOURCE_TYPES = new Set(['select', 'country']);

function KycStepDependencyModal({
  isOpen,
  error,
  modalState,
  candidateFields,
  sourceField,
  sourceOptions,
  onClose,
  onFieldChange,
  onValuesTextChange,
  onSelectValuesChange,
  onRadioValueChange,
  onCheckboxStateChange,
  onClear,
  onSave,
  showClear = true,
}) {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  const selectedSelectValues = Array.isArray(modalState.selectValues) ? modalState.selectValues : [];
  const selectedTags = selectedSelectValues.map((option) => ({ value: option, label: option }));
  const suggestions = sourceOptions.map((option) => ({ value: option, label: option }));

  const handleSelectTagDelete = (tagIndex) => {
    onSelectValuesChange(selectedSelectValues.filter((_, index) => index !== tagIndex));
  };

  const handleSelectTagAdd = (newTag) => {
    const rawValue = typeof newTag?.value === 'string' && newTag.value.trim().length > 0
      ? newTag.value
      : (typeof newTag?.label === 'string' ? newTag.label : '');
    const nextValue = rawValue.trim();

    if (!nextValue || selectedSelectValues.includes(nextValue)) {
      return;
    }

    onSelectValuesChange([...selectedSelectValues, nextValue]);
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('kyc.dependencyTitle')}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="mb-3">
                <label className="form-label">{t('kyc.dependsOnField')}</label>
                <select
                  className="form-select"
                  value={modalState.fieldId}
                  onChange={(event) => onFieldChange(event.target.value)}
                >
                  <option value="">{t('kyc.selectField')}</option>
                  {candidateFields.map((field) => (
                    <option key={field.fieldId} value={field.fieldId}>
                      {field.name || t('kyc.unnamed')} ({field.type})
                    </option>
                  ))}
                </select>
              </div>

              {sourceField && (sourceField.type === 'text' || sourceField.type === 'date') && (
                <div className="mb-3">
                  <label className="form-label">{t('kyc.valuesOneLine')}</label>
                  <textarea
                    rows="4"
                    className="form-control"
                    value={modalState.valuesText}
                    onChange={(event) => onValuesTextChange(event.target.value)}
                  />
                </div>
              )}

              {sourceField && MULTI_VALUE_SOURCE_TYPES.has(sourceField.type) && (
                <div className="mb-3">
                  <label className="form-label">{t('kyc.valuesLabel')}</label>
                  <ReactTags
                    selected={selectedTags}
                    suggestions={suggestions}
                    allowNew={false}
                    onDelete={handleSelectTagDelete}
                    onAdd={handleSelectTagAdd}
                    placeholderText={t('kyc.valuePlaceholder')}
                  />
                </div>
              )}

              {sourceField && sourceField.type === 'radio' && (
                <div className="mb-3">
                  <label className="form-label">{t('kyc.valueLabel')}</label>
                  <select
                    className="form-select"
                    value={modalState.radioValue}
                    onChange={(event) => onRadioValueChange(event.target.value)}
                  >
                    <option value="">{t('kyc.selectValue')}</option>
                    {sourceOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}

              {sourceField && sourceField.type === 'checkbox' && (
                <div className="mb-3">
                  <label className="form-label">{t('kyc.checkboxState')}</label>
                  <select
                    className="form-select"
                    value={modalState.checkboxState}
                    onChange={(event) => onCheckboxStateChange(event.target.value)}
                  >
                    <option value="checked">{t('kyc.checkboxChecked')}</option>
                    <option value="unchecked">{t('kyc.checkboxUnchecked')}</option>
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {showClear && (
                <button type="button" className="btn btn-danger me-auto" onClick={onClear}>
                  {t('kyc.clearDependency')}
                </button>
              )}
              <button type="button" className="btn btn-default" onClick={onClose}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-theme" onClick={onSave}>{t('kyc.saveDependency')}</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default KycStepDependencyModal;
